import DOMPurify from 'dompurify';

import type { MailAttachmentInfo } from './types';

type DOMPurifyFactory = (win: Window) => typeof DOMPurify;

function getCurrentDocumentOrigin(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

function buildMailHtmlCsp(): string {
  const imageSources = ["'self'", 'data:', 'blob:'];
  const origin = getCurrentDocumentOrigin();
  if (origin) {
    imageSources.push(origin);
  }
  return [
    "default-src 'none'",
    "script-src 'none'",
    "connect-src 'none'",
    "frame-src 'none'",
    "object-src 'none'",
    "base-uri 'none'",
    "form-action 'none'",
    `img-src ${Array.from(new Set(imageSources)).join(' ')}`,
    'font-src data:',
    "style-src 'unsafe-inline'",
  ].join('; ');
}

const FORBIDDEN_MAIL_HTML_TAGS = [
  'base',
  'button',
  'embed',
  'form',
  'foreignObject',
  'iframe',
  'input',
  'link',
  'math',
  'meta',
  'object',
  'script',
  'select',
  'style',
  'svg',
  'textarea',
];
const FORBIDDEN_MAIL_HTML_CONTENT_TAGS = FORBIDDEN_MAIL_HTML_TAGS.filter(
  tag => tag !== 'svg'
);

const DEFAULT_REMOTE_IMAGE_PLACEHOLDER_LABEL = 'Remote image blocked';

function getPurify(): typeof DOMPurify {
  const factory = DOMPurify as unknown as DOMPurifyFactory & typeof DOMPurify;
  return typeof window !== 'undefined' ? factory(window) : factory;
}

function stripUnsupportedEmbeddedMarkup(rawHtml: string): string {
  const withoutEmbeddedBlocks = rawHtml
    .replace(/<svg\b[\s\S]*?<\/svg\s*>/giu, '')
    .replace(/<math\b[\s\S]*?<\/math\s*>/giu, '');
  if (typeof document === 'undefined') return withoutEmbeddedBlocks;
  const template = document.createElement('template');
  template.innerHTML = withoutEmbeddedBlocks;
  template.content.querySelectorAll('svg, math').forEach(element => {
    element.remove();
  });
  return template.innerHTML;
}

function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/gu, '&amp;')
    .replace(/"/gu, '&quot;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;');
}

function normalizeContentId(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/^<|>$/gu, '').toLowerCase();
}

function getAttachmentBlobUrl(attachment: MailAttachmentInfo): string {
  return `/api/workspaces/${encodeURIComponent(
    attachment.workspaceId
  )}/blobs/${encodeURIComponent(attachment.blobKey)}`;
}

function findCidAttachment(
  src: string,
  attachments: MailAttachmentInfo[]
): MailAttachmentInfo | null {
  const cid = normalizeContentId(src.replace(/^cid:/iu, ''));
  if (!cid) return null;
  return (
    attachments.find(
      attachment => normalizeContentId(attachment.contentId) === cid
    ) ?? null
  );
}

function isRemoteImageSrc(src: string): boolean {
  return /^https?:\/\//iu.test(src.trim());
}

function normalizeRemoteImageUrl(src: string): string {
  return src.trim();
}

export function getMailRemoteImageId(src: string): string {
  let hash = 0xcbf29ce484222325n;
  const normalized = normalizeRemoteImageUrl(src);
  for (let index = 0; index < normalized.length; index += 1) {
    hash ^= BigInt(normalized.charCodeAt(index));
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  return `mi_${hash.toString(16).padStart(16, '0')}`;
}

function normalizeImageDimension(value: string | null): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  if (/^\d{1,4}$/u.test(trimmed)) return `${trimmed}px`;
  if (/^\d{1,4}(?:\.\d{1,2})?(?:px|em|rem|%)$/iu.test(trimmed)) {
    return trimmed;
  }
  return null;
}

function appendPlaceholderDimension(
  placeholder: HTMLElement,
  property: 'width' | 'height',
  value: string | null
) {
  const dimension = normalizeImageDimension(value);
  if (dimension) {
    placeholder.style[property] = dimension;
  }
}

function parsePixelDimension(value: string | null): number | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const match = /^(\d{1,4})(?:px)?$/iu.exec(trimmed);
  if (!match) return null;
  return Number(match[1]);
}

function isTinyRemoteImage(image: HTMLImageElement): boolean {
  const width = parsePixelDimension(image.getAttribute('width'));
  const height = parsePixelDimension(image.getAttribute('height'));
  return width !== null && height !== null && width <= 4 && height <= 4;
}

function createBlockedRemoteImageIcon(document: Document): SVGSVGElement {
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('data-mail-remote-image-icon', '');
  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('focusable', 'false');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.8');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  path.setAttribute(
    'd',
    'M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v13A1.5 1.5 0 0 1 18.5 20h-13A1.5 1.5 0 0 1 4 18.5zM8 9.5h.01M20 16l-4.8-4.8a1.2 1.2 0 0 0-1.7 0L6 18'
  );

  icon.append(path);
  return icon;
}

function replaceRemoteImageWithPlaceholder(
  image: HTMLImageElement,
  remoteImage: MailRemoteImage,
  label: string
) {
  const placeholder = document.createElement('span');
  const alt = remoteImage.alt.trim();
  const accessibilityLabel = alt ? `${alt}. ${label}` : label;
  const isTinyImage = isTinyRemoteImage(image);
  placeholder.setAttribute('data-mail-remote-image', 'blocked');
  placeholder.setAttribute('data-mail-remote-image-id', remoteImage.id);
  placeholder.setAttribute('role', 'img');
  placeholder.setAttribute('aria-label', accessibilityLabel);
  placeholder.setAttribute('title', alt || label);

  if (isTinyImage) {
    placeholder.setAttribute('data-mail-remote-image-size', 'tiny');
    placeholder.style.width = '1px';
    placeholder.style.height = '1px';
  } else {
    appendPlaceholderDimension(
      placeholder,
      'width',
      image.getAttribute('width')
    );
    appendPlaceholderDimension(
      placeholder,
      'height',
      image.getAttribute('height')
    );
    placeholder.append(createBlockedRemoteImageIcon(document));
  }

  image.replaceWith(placeholder);
}

export interface MailRemoteImage {
  id: string;
  src: string;
  alt: string;
}

export interface SanitizeMailHtmlOptions {
  remoteImagePlaceholderLabel?: string;
  resolveRemoteImageUrl?: (image: MailRemoteImage) => string | null;
}

function hardenMailHtmlDocument(
  sanitizedHtml: string,
  attachments: MailAttachmentInfo[],
  options: SanitizeMailHtmlOptions
): {
  html: string;
  blockedRemoteImages: number;
  remoteImages: MailRemoteImage[];
} {
  if (typeof document === 'undefined') {
    return { html: sanitizedHtml, blockedRemoteImages: 0, remoteImages: [] };
  }

  const template = document.createElement('template');
  template.innerHTML = sanitizedHtml;
  let blockedRemoteImages = 0;
  const remoteImages = new Map<string, MailRemoteImage>();

  template.content
    .querySelectorAll(FORBIDDEN_MAIL_HTML_TAGS.join(','))
    .forEach(element => element.remove());

  template.content.querySelectorAll('*').forEach(element => {
    for (const attribute of Array.from(element.attributes)) {
      if (/^on/iu.test(attribute.name)) {
        element.removeAttribute(attribute.name);
        continue;
      }
      if (
        /^(?:href|src)$/iu.test(attribute.name) &&
        /^\s*javascript:/iu.test(attribute.value)
      ) {
        element.removeAttribute(attribute.name);
      }
    }
  });

  template.content.querySelectorAll('a').forEach(link => {
    const href = link.getAttribute('href')?.trim() ?? '';
    if (!href || !/^(https?:|mailto:|tel:)/iu.test(href)) {
      link.removeAttribute('href');
      return;
    }
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noreferrer noopener');
  });

  template.content.querySelectorAll('img').forEach(image => {
    image.removeAttribute('srcset');
    const src = image.getAttribute('src')?.trim() ?? '';
    if (!src) return;

    if (/^cid:/iu.test(src)) {
      const attachment = findCidAttachment(src, attachments);
      if (attachment) {
        image.setAttribute('src', getAttachmentBlobUrl(attachment));
      } else {
        image.removeAttribute('src');
      }
      return;
    }

    if (isRemoteImageSrc(src)) {
      const remoteImage = {
        id: getMailRemoteImageId(src),
        src: normalizeRemoteImageUrl(src),
        alt: image.getAttribute('alt') ?? '',
      };
      remoteImages.set(remoteImage.id, remoteImage);
      const proxyUrl = options.resolveRemoteImageUrl?.(remoteImage);
      if (proxyUrl) {
        image.setAttribute('src', proxyUrl);
        image.setAttribute('loading', 'lazy');
        image.setAttribute('referrerpolicy', 'no-referrer');
        return;
      }
      blockedRemoteImages += 1;
      replaceRemoteImageWithPlaceholder(
        image,
        remoteImage,
        options.remoteImagePlaceholderLabel ??
          DEFAULT_REMOTE_IMAGE_PLACEHOLDER_LABEL
      );
      return;
    }

    if (!/^data:image\//iu.test(src) && !/^blob:/iu.test(src)) {
      image.removeAttribute('src');
    }
  });

  template.content.querySelectorAll<HTMLElement>('[style]').forEach(element => {
    const style = element.getAttribute('style') ?? '';
    if (/(?:url\s*\(|@import|expression\s*\()/iu.test(style)) {
      element.removeAttribute('style');
    }
  });

  return {
    html: template.innerHTML.trim(),
    blockedRemoteImages,
    remoteImages: Array.from(remoteImages.values()),
  };
}

export interface SanitizedMailHtml {
  html: string;
  srcDoc: string;
  blockedRemoteImages: number;
  remoteImages: MailRemoteImage[];
}

export function buildMailHtmlSrcDoc(
  html: string,
  options: {
    backgroundColor?: string;
    bodyPaddingPx?: number;
  } = {}
): string {
  const backgroundColor = options.backgroundColor ?? '#fff';
  const bodyPadding =
    options.bodyPaddingPx === undefined
      ? '22px 24px'
      : `${Math.max(0, options.bodyPaddingPx)}px`;

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8">
    <meta http-equiv="Content-Security-Policy" content="${escapeHtmlAttribute(buildMailHtmlCsp())}">
    <style>
      html {
        width: 100%;
        min-width: 0;
        color-scheme: light;
        background-color: ${backgroundColor};
      }
      body {
        box-sizing: border-box;
        width: 100%;
        min-width: 0;
        margin: 0;
        padding: ${bodyPadding};
        overflow-x: hidden;
        overflow-wrap: anywhere;
        color: #1f2328;
        background-color: ${backgroundColor};
        font: 14px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      }
      img {
        max-width: 100%;
        height: auto;
      }
      [data-mail-remote-image="blocked"] {
        box-sizing: border-box;
        display: inline-grid;
        place-items: center;
        min-width: 120px;
        max-width: 100%;
        min-height: 44px;
        margin: 2px 0;
        padding: 0;
        border: 1px solid rgba(31, 35, 40, 0.10);
        border-radius: 6px;
        background: #f1f3f5;
        color: #98a2b3;
        vertical-align: middle;
        overflow: hidden;
      }
      [data-mail-remote-image-icon] {
        width: 20px;
        height: 20px;
        opacity: 0.72;
      }
      [data-mail-remote-image-size="tiny"] {
        display: inline-block;
        min-width: 0;
        min-height: 0;
        margin: 0;
        padding: 0;
        border: 0;
        background: transparent;
        color: transparent;
      }
      table {
        width: 100% !important;
        max-width: 100% !important;
        border-collapse: collapse;
      }
      tbody,
      thead,
      tfoot,
      tr,
      td,
      th {
        max-width: 100% !important;
      }
      a {
        color: #0969da;
      }
      pre {
        white-space: pre-wrap;
      }
      #sl-mail-fit-root {
        box-sizing: border-box;
        width: 100%;
        min-width: 0;
        transform-origin: top left;
      }
    </style>
  </head>
  <body><div id="sl-mail-fit-root">${html}</div></body>
</html>`;
}

export function sanitizeMailHtml(
  rawHtml: string | null | undefined,
  attachments: MailAttachmentInfo[] = [],
  options: SanitizeMailHtmlOptions = {}
): SanitizedMailHtml | null {
  if (!rawHtml?.trim()) return null;

  const sanitized = getPurify().sanitize(
    stripUnsupportedEmbeddedMarkup(rawHtml),
    {
      ALLOW_DATA_ATTR: true,
      ADD_ATTR: ['target', 'rel'],
      FORBID_ATTR: ['srcset'],
      FORBID_CONTENTS: FORBIDDEN_MAIL_HTML_CONTENT_TAGS,
      FORBID_TAGS: FORBIDDEN_MAIL_HTML_TAGS,
      ALLOWED_URI_REGEXP:
        /^(?:(?:https?|mailto|tel|cid|data):|[^a-z]|[-a-z+.]+(?:[^-a-z+.:]|$))/iu,
    }
  );
  const html = typeof sanitized === 'string' ? sanitized : String(sanitized);
  const hardened = hardenMailHtmlDocument(html, attachments, options);
  if (!hardened.html) return null;

  return {
    ...hardened,
    srcDoc: buildMailHtmlSrcDoc(hardened.html),
  };
}
